import { Injectable } from "@angular/core";
import { Observable, of, map } from 'rxjs';
import { MeasurementSystem, measurementSystemsWeightMapping } from "@me-fit-mono/typings";
import { AuthService } from "./auth.service";

/**
 * Will only include the preferred measurement system of the current organization.
 * */
@Injectable({
  providedIn: 'root'
})
export class PreferredMeasurementService {
  constructor(private $auth: AuthService) {}

  private defaultMeasurementSystem: MeasurementSystem = 'metric';

  preferredMeasurementSystem$: Observable<MeasurementSystem> = this.$auth.organization$.pipe(
    map( organization => {
      if (!organization) {
        return this.defaultMeasurementSystem;
      }

      return organization.preferredMeasurementSystem;
    })
  );

  weightUnitOfMeasure$ = this.preferredMeasurementSystem$.pipe(
    map( measurementSystem => {
      return measurementSystemsWeightMapping[measurementSystem];
    })
  );

  heightUnitOfMeasure$ = this.preferredMeasurementSystem$.pipe(
    map( measurementSystem => {
      const measurementSystemsHeightMapping: Record<MeasurementSystem, string> = {
        'metric': 'cm',
        'imperial': 'in'
      }

      return measurementSystemsHeightMapping[measurementSystem];
    })
  );

  getWeightForMeasurementSystem(weightValue: number, measurementSystem: MeasurementSystem): string {
    if (measurementSystem === 'metric') {
      // Return as is, because we store values in kg
      return `${weightValue.toFixed(2)} kg`;
    }

    // Convert to pounds
    return `${(weightValue * 2.20462).toFixed(2)} lb`;
  }

  getHeightForMeasurementSystem(heightValue: number, measurementSystem: MeasurementSystem): string {
    if (measurementSystem === 'metric') {
      // Return as is, because we store values in cm
      return heightValue.toFixed(2) + ' cm';
    }

    // Convert to feet and inches
    const totalInches = heightValue * 0.393701;
    const feet = Math.floor(totalInches / 12);
    const inches = (totalInches % 12).toFixed(2);
    return `${feet} ft ${inches} in`;
  }
}
